import {
	BAD_REQUEST,
	CONFLICT,
	FORBIDDEN,
	NO_CONTENT,
	TOO_MANY_REQUESTS,
	UNAUTHORIZED,
	UNPROCESSABLE_ENTITY,
} from '@jira/platform__http-status-codes/src/http-status-codes.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import { transformValidationErrorFromServer } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { getTraceId } from '@atlassian/jira-fetch/src/utils/trace-id.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processResponseFromServer = (response: Response): Promise<any> => {
	if (response.status === NO_CONTENT) {
		return Promise.resolve(null);
	}
	return response.text().then((text) => (text ? JSON.parse(text) : null));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleNetworkErrors = (err: Error): Promise<any> => {
	if (err instanceof TypeError) {
		throw new Error('Failed to fetch', { cause: err });
	}
	throw err;
};

const handleErrors = async (response: Response): Promise<Response> => {
	const clone = response.clone();
	const res = await response.json();
	const traceId = getTraceId(response);
	// handle CONFLICT and TOO_MANY_REQUESTS using validation error handlers.
	if (
		[BAD_REQUEST, CONFLICT, TOO_MANY_REQUESTS, UNAUTHORIZED, FORBIDDEN, UNPROCESSABLE_ENTITY].find(
			(i) => i === response.status,
		)
	) {
		throw transformValidationErrorFromServer(res, response.status, traceId);
	}
	if (res?.errorMessages) {
		throw res.errorMessages.join('\n');
	}
	if (!response.ok) {
		if (traceId !== undefined && traceId.length > 0) {
			throw new FetchError(response.status, `Error server response: ${response.status}`, traceId);
		} else {
			throw new FetchError(response.status, `Error server response: ${response.status}`);
		}
	}
	return clone;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const performPost = <ResponseType = any,>(
	url: string,
	options: RequestInit = {},
): Promise<ResponseType> => {
	const defaultOptions = getDefaultOptions(url);

	const mergedOpts = {
		...defaultOptions,
		...options,
		method: 'POST',
		headers: {
			...defaultOptions.headers,
			...options.headers,
		},
	};

	return fetch(url, mergedOpts)
		.then(handleErrors)
		.then(processResponseFromServer)
		.catch(handleNetworkErrors);
};
